import React, {useCallback, useEffect, useRef, useState, useMemo} from 'react';
import _ from 'underscore';

import {dispatcher} from '@netx/core-next/instance/dispatcher';

// @ts-ignore
import {default as hypergrid} from 'core-libs/fin-hypergrid/fin-hypergrid';
// Do not remove
// If we do not do anything with this it gets discarded in builds
// you can console.log or assign it to a variable to keep it around
// `hypergrid` itself resolves to be `{}` - they install it in the
// `global.fin` namespace
const someWeirdTreeShakingIsHappening = hypergrid;

import columns from '../../columns';
// import {GridEditor, GridEditorProps} from './containers';
import {cellKeyboardNavigationHack, scrollHack} from '../../hacks';
import {AttributeSetSelectionProvider} from '@netx/core-attribute-set-selection';
import {AttributeSetsProvider} from '@netx/core-attribute-set';
import {AttributeProvider} from '@netx/core-attribute';
import GridEditor, {GridEditorProps} from '../../components/GridEditor';
import {
	ChangeStoreProvider,
	GridStoreProvider,
	ProgressStoreProvider,
	SelectionStoreProvider,
} from '../../store';
import {ColumnSchema, DataRow, GridEditor as GridEditorType} from '../../types';
import properties from '@netx/core-grid/properties/grid';
import {getDefaults} from '@netx/core-grid/lib/configs/default';
import ClientSort from '@netx/core-grid/lib/sorters/client';
import ServerSort from '@netx/core-grid/lib/sorters/server';
import {withErrorBoundary} from '@netx/core-components/ErrorBoundary';

const Sorters = {
	client: ClientSort,
	server: ServerSort,
};

export type GridEditorContainerProps = GridEditorProps;

const GridEditorContainer = ({
	onChange,
	...props
}: GridEditorContainerProps) => {
	const elementRef = useRef<HTMLDivElement>(null);
	const pluginRef = useRef<GridEditorType>();
	const configurationRef = useRef<Record<string, any>>({});
	const [pluginReady, setPluginReady] = useState(false);

	const schemaRef = useRef<Record<string, any>[]>([]);
	const columnsRef = useRef<ReturnType<typeof columns>>();
	const [hasChanges, setHasChanges] = useState(false);

	const defaultSelection = useMemo(() => ['all'], []);

	// Setup hypergrid plugin
	useEffect(() => {
		if (!elementRef.current) {
			setPluginReady(false);
			return;
		}

		const plugin: GridEditorType = (pluginRef.current =
			// @ts-ignore
			new global.fin.Hypergrid(elementRef.current, {
				data: [],
			}));

		cellKeyboardNavigationHack(plugin);
		scrollHack(plugin);

		plugin.addProperties(properties);

		const updateConfiguration = (type?: string) => {
			configurationRef.current = {...getDefaults(type), fixedColumnCount: 3};
			plugin.setState(configurationRef.current);
		};

		updateConfiguration();

		// Setup columns
		const _columns = (columnsRef.current = columns(plugin));

		const themeListener = (type: string) => {
			updateConfiguration(type);
			// Have to resetup schema after updating the configuration
			_columns.updateSchema(schemaRef.current);
		};
		dispatcher.on('theme:toggled', themeListener);

		setPluginReady(true);

		return () => {
			dispatcher.off('theme:toggled', themeListener);
			try {
				pluginRef.current = undefined;
				plugin.removeAllEventListeners();
				plugin.terminate();
				while (plugin.div.hasChildNodes()) {
					plugin.div.lastChild && plugin.div.removeChild(plugin.div.lastChild);
				}
			} catch (ex) {}
		};
	}, []);

	const handleSetData = useCallback((data: DataRow[], schema: ColumnSchema) => {
		const plugin = pluginRef.current;
		if (!plugin) return;

		plugin.setData(data, {
			schema: schema.map((schema) => {
				return {
					header: schema.header,
					name: schema.name,
				};
			}),
		});

		columnsRef.current?.updateSchema((schemaRef.current = schema));

		plugin.installPlugins(Sorters.client);

		setTimeout(() => {
			plugin.fireSyntheticGridResizedEvent(new Event('resize'));
		}, 51);
	}, []);

	const handleChange = useCallback(
		(hasChanges: boolean) => {
			setHasChanges(hasChanges);
			onChange?.(hasChanges);
		},
		[onChange],
	);

	return (
		<AttributeProvider>
			<AttributeSetsProvider objectType="asset">
				<AttributeSetSelectionProvider
					confirm={hasChanges}
					defaultSelection={defaultSelection}
					userPrefKey="attribute.attributeSets.selected.tagPanelGrid"
				>
					<ProgressStoreProvider>
						<ChangeStoreProvider gridEditor={pluginRef.current}>
							<SelectionStoreProvider gridEditor={pluginRef.current}>
								<GridStoreProvider
									gridEditor={pluginRef.current}
									onSetData={handleSetData}
								>
									<GridEditor
										onChange={handleChange}
										{...props}
										ref={elementRef}
									/>
								</GridStoreProvider>
							</SelectionStoreProvider>
						</ChangeStoreProvider>
					</ProgressStoreProvider>
				</AttributeSetSelectionProvider>
			</AttributeSetsProvider>
		</AttributeProvider>
	);
};

export default withErrorBoundary(GridEditorContainer);
